/**
 * A client's services — the client page's Services tab.
 *
 * The list is one card per service: the provider, whether its credentials were
 * accepted, whether it is the channel's primary, and what it holds. Opening one
 * manages it in place, with its connection, sender IDs and templates on
 * sub-tabs. The open service is in the URL, like the tab, so a link to "Acme's
 * SMS templates" is a link.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  BellRing,
  Bot,
  ChevronRight,
  Inbox,
  KeyRound,
  Webhook,
  Layers,
  Mail,
  MailX,
  MessagesSquare,
  Plus,
  Send,
  SendHorizontal,
  ShieldCheck,
  Star,
  PhoneCall,
  Wallet,
  ReceiptText,
  Link2,
} from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  LoadingState,
  Sections,
  Tabs,
  formatMoney,
  useCan,
  useHasService,
} from '@connect/web-shared';

import { AddServiceDialog } from './add-service-dialog';
import { ChannelIcon } from './channel-icon';
import { ChannelSendersCard } from './channel-senders-card';
import { ChannelTemplatesCard } from './channel-templates-card';
import { ConnectChatSitesCard } from './connectchat-sites-card';
import { ServiceConnectionCard } from './service-connection-card';
import { AiCard } from './ai-card';
import { BillingCard } from './billing-card';
import { ClientBillingArea } from './client-billing-area';
import { PushPriceCard } from './push-price-card';
import { RcsWalletCard } from './rcs-wallet-card';
import { CallingClientCard } from './calling-client-card';
import { EmailSendingCard, EmailSuppressionCard, EmailWalletCard } from './email-client-cards';
import { EmailInboundCard } from './email-inbound';
import { ClientWebhooksCard } from './client-webhooks-card';
import { IntegrationsCard } from './integrations-card';
import { ClientConnectedAppsCard } from './client-connected-apps-card';
import { OtpCard } from './otp-card';
import { SendingCard } from './sending-card';
import { SuppressionCard } from './suppression-card';
import {
  channelHealth,
  fetchCatalog,
  fetchClientChannels,
  findProvider,
  messagingKeys,
  type ChannelDef,
  type ClientChannel,
} from './types';

export function ClientServicesPanel({
  clientPublicId,
  clientName,
}: {
  clientPublicId: string;
  clientName: string;
}) {
  const hasIntegrations = useHasService('integrations');
  // Each section is here only for somebody who may see it — absent, never empty.
  const canSending = useCan('messaging.channel.view');
  const canSuppression = useCan('messaging.suppression.view');
  const canIntegrations = useCan('messaging.integration.view');
  const canWebhooks = useCan('messaging.webhook.view');
  const canOtp = useCan('messaging.otp.view');
  const canAi = useCan('messaging.ai.view');
  const canRcs = useCan('messaging.rcs.view');
  const canPush = useCan('messaging.push.view');
  const canEmail = useCan('messaging.email.view');
  const canCalling = useCan('messaging.calling.view');
  const canBilling = useCan('messaging.billing.view');
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);

  const catalog = useQuery({
    queryKey: messagingKeys.catalog,
    queryFn: fetchCatalog,
    // The catalog is the build's, and changes only with a deploy.
    staleTime: Infinity,
  });
  const channels = useQuery({
    queryKey: messagingKeys.channels(clientPublicId),
    queryFn: () => fetchClientChannels(clientPublicId),
  });

  const openId = params.get('service');
  const setOpen = (id: string | null, section?: string) =>
    setParams(
      (previous) => {
        const search = new URLSearchParams(previous);
        search.delete('section');
        if (id) search.set('service', id);
        else search.delete('service');
        if (id && section) search.set('section', section);
        return search;
      },
      { replace: false },
    );

  if (catalog.isLoading || channels.isLoading) return <LoadingState label="Loading services" />;
  const failed = catalog.error ?? channels.error;
  if (failed || !catalog.data || !channels.data) {
    return (
      <ErrorState
        message={failed instanceof ApiError ? failed.message : 'The services could not be loaded.'}
        onRetry={() => {
          void catalog.refetch();
          void channels.refetch();
        }}
      />
    );
  }

  // Providers whose messages Connect bills from the client's wallet.
  const prepaid = new Set(catalog.data.flatMap((def) => def.providers).filter((p) => p.prepaid_wallet).map((p) => p.id));

  // Email is in the catalog only on an installation that offers it; elsewhere
  // the staff email routes do not exist, so nothing here may ask them.
  const emailOffered = catalog.data.some((def) => def.id === 'email');
  const hasEmail = channels.data.some((c) => c.channel === 'email');

  const open = channels.data.find((c) => c.public_id === openId);
  if (open) {
    return (
      <ServiceManager
        channel={open}
        catalog={catalog.data}
        clientName={clientName}
        section={params.get('section') ?? 'connection'}
        onSection={(section) => setOpen(open.public_id, section)}
        onBack={() => setOpen(null)}
      />
    );
  }

  const addButton = (
    <Can permission="messaging.channel.create">
      <Button size="sm" onClick={() => setAdding(true)}>
        <Plus className="size-3.5" />
        Add service
      </Button>
    </Can>
  );

  return (
    <div className="space-y-3">
      {channels.data.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title={`${clientName} has no services yet`}
              description="Add a service to connect this client's own account with a provider — its sender IDs and templates follow."
              action={addButton}
            />
          </CardBody>
        </Card>
      ) : (
        <Sections
          // `section` is taken: it is the open service's own tab.
          param="area"
          label="Services and settings"
          items={[
            {
              id: 'services',
              label: 'Services',
              hint: 'Accounts with providers',
              icon: <Layers />,
              badge: <span className="text-2xs text-faint tabular">{channels.data.length}</span>,
              content: (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-muted">
                      {channels.data.length} service{channels.data.length === 1 ? '' : 's'} for {clientName}
                    </p>
                    {addButton}
                  </div>
                  {catalog.data
                    .filter((def) => channels.data.some((c) => c.channel === def.id))
                    .map((def) => (
                      <ChannelGroup
                        key={def.id}
                        def={def}
                        services={channels.data.filter((c) => c.channel === def.id)}
                        onOpen={(id) => setOpen(id)}
                      />
                    ))}
                </>
              ),
            },
            // Whether anything this client sends leaves at all.
            ...(canSending
              ? [{ id: 'sending', label: 'Sending', hint: 'On, or a demo', icon: <Send />, content: <SendingCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Per client, not per service: a number that opted out did so from
            // the client, whichever of its accounts the message would come from.
            ...(canSuppression
              ? [{ id: 'suppression', label: 'Suppression list', hint: 'Numbers never messaged', icon: <Ban />, content: <SuppressionCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Integrations are a service of their own: switched off for the
            // installation, the section is not here for anybody.
            ...(hasIntegrations && canIntegrations
              ? [{ id: 'api', label: 'API access', hint: 'Keys', icon: <KeyRound />, content: <IntegrationsCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Connected apps: their own switch, apart from API access (docs/44).
            ...(hasIntegrations && canIntegrations
              ? [{ id: 'connected-apps', label: 'Connected apps', hint: 'Sign in with Connect', icon: <Link2 />, content: <ClientConnectedAppsCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Webhooks: their own switch, apart from API keys (docs/39).
            ...(hasIntegrations && canWebhooks
              ? [{ id: 'webhooks', label: 'Webhooks', hint: 'Switches, limits, endpoints', icon: <Webhook />, content: <ClientWebhooksCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            ...(canOtp
              ? [{ id: 'otp', label: 'One-time codes', hint: 'Verification codes', icon: <ShieldCheck />, content: <OtpCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // The one wallet every service is paid from, and SMS credits — for
            // a client with any service, or credited ahead of its first.
            ...(canBilling
              ? [
                  { id: 'billing', label: 'Wallet & credits', hint: 'Balance, top-ups, SMS credits', icon: <Wallet />, content: <BillingCard clientPublicId={clientPublicId} clientName={clientName} /> },
                  // What the client's Billing area shows it, with what only
                  // staff see: the balance check and the models (docs/28 §11).
                  { id: 'statements', label: 'Statements', hint: 'Spend by day and month', icon: <ReceiptText />, content: <ClientBillingArea clientPublicId={clientPublicId} clientName={clientName} /> },
                ]
              : []),
            // One assistant per client, paid from its one wallet, shared by every
            // service it has (owner, 2026-10-16): WhatsApp, RCS, ConnectChat,
            // and the AI writing help under any of them. So it is here for a
            // client with any service, not only one with WhatsApp.
            ...(canAi && channels.data.length > 0
              ? [{ id: 'ai', label: 'AI assistant', hint: 'Prices and account help', icon: <Bot />, content: <AiCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // RCS's prices, for a client with a service Connect bills (RCS).
            ...(canRcs && channels.data.some((c) => prepaid.has(c.provider))
              ? [{ id: 'rcs', label: 'RCS prices', hint: 'And what RCS took', icon: <MessagesSquare />, content: <RcsWalletCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Push's price and what push took, for a client with a push service.
            ...(canPush && channels.data.some((c) => c.channel === 'push')
              ? [{ id: 'push', label: 'Push price', hint: 'And what push took', icon: <BellRing />, content: <PushPriceCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Calls are about a client's callbacks, whichever service booked
            // them: here for any client, as the assistant is.
            ...(canCalling
              ? [{ id: 'calls', label: 'Automated calls', hint: 'Callbacks by phone', icon: <PhoneCall />, content: <CallingClientCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Email's price wherever email is offered: set before a client's
            // service is added, as a trial credit to its wallet is.
            ...(canEmail && emailOffered
              ? [{ id: 'email', label: 'Email price', hint: 'And what email took', icon: <Mail />, content: <EmailWalletCard clientPublicId={clientPublicId} clientName={clientName} /> }]
              : []),
            // Its stops and its list only once it has an email service: before
            // that there is no email to stop and nobody to skip.
            ...(canEmail && emailOffered && hasEmail
              ? [
                  { id: 'email-sending', label: 'Email sending', hint: 'Pause it, or a domain', icon: <SendHorizontal />, content: <EmailSendingCard clientPublicId={clientPublicId} clientName={clientName} /> },
                  { id: 'email-suppression', label: 'Do-not-email list', hint: 'Addresses never emailed', icon: <MailX />, content: <EmailSuppressionCard clientPublicId={clientPublicId} clientName={clientName} /> },
                  { id: 'email-inbound', label: 'Incoming email', hint: 'Forwarded mail, its price', icon: <Inbox />, content: <EmailInboundCard clientPublicId={clientPublicId} clientName={clientName} /> },
                ]
              : []),
          ]}
        />
      )}

      <AddServiceDialog
        clientPublicId={clientPublicId}
        clientName={clientName}
        catalog={catalog.data}
        existing={channels.data}
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(created) => setOpen(created.public_id)}
      />
    </div>
  );
}

function ChannelGroup({
  def,
  services,
  onOpen,
}: {
  def: ChannelDef;
  services: ClientChannel[];
  onOpen: (id: string) => void;
}) {
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
        <ChannelIcon channel={def.id} className="size-3.5" />
        {def.label}
      </h3>
      <div className="grid gap-3 md:grid-cols-2">
        {services.map((service) => (
          <ServiceCard key={service.public_id} service={service} def={def} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({
  service,
  def,
  onOpen,
}: {
  service: ClientChannel;
  def: ChannelDef;
  onOpen: (id: string) => void;
}) {
  const health = channelHealth(service);
  return (
    <button
      type="button"
      onClick={() => onOpen(service.public_id)}
      className="group w-full rounded-card border border-line bg-surface p-4 text-left shadow-card transition-colors hover:border-accent focus-visible:border-accent focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-accent-subtle text-accent">
            <ChannelIcon channel={service.channel} />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="truncate text-sm font-medium text-heading">
              {service.label ?? service.provider_label}
            </p>
            {service.label ? <p className="text-xs text-muted">{service.provider_label}</p> : null}
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={health.tone}>{health.label}</Badge>
              {service.is_primary ? (
                <Badge tone="accent">
                  <Star className="size-2.5" />
                  Primary
                </Badge>
              ) : null}
              {service.low_balance ? <Badge tone="warning">Low balance</Badge> : null}
            </div>
          </div>
        </div>
        <ChevronRight className="size-4 shrink-0 text-faint transition-colors group-hover:text-accent" />
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-line pt-3 text-xs">
        <Stat label="Balance">
          {service.account_status?.balance
            ? formatMoney(service.account_status.balance, service.account_status.currency ?? 'INR')
            : '—'}
        </Stat>
        <Stat label={`${def.sender_label}s`}>{service.sender_count}</Stat>
        <Stat label="Templates">
          {service.template_count
            ? `${service.templates_ready}/${service.template_count} ready`
            : 0}
        </Stat>
      </dl>
    </button>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-2xs text-faint">{label}</dt>
      <dd className="truncate tabular text-body">{children}</dd>
    </div>
  );
}

function ServiceManager({
  channel,
  catalog,
  clientName,
  section,
  onSection,
  onBack,
}: {
  channel: ClientChannel;
  catalog: ChannelDef[];
  clientName: string;
  section: string;
  onSection: (section: string) => void;
  onBack: () => void;
}) {
  const canSenders = useCan('messaging.sender.view');
  const canTemplates = useCan('messaging.template.view');
  const found = findProvider(catalog, channel.provider);
  const health = channelHealth(channel);
  // A ConnectChat service's senders are its websites and apps, managed as
  // sites; and it has no templates, so no Templates tab.
  const isWebchat = channel.channel === 'webchat';
  // A push service has no senders: its websites and apps are the client's
  // own, added in the client's portal; the API refuses one made here.
  const hasSenders = channel.channel !== 'push';
  const hasTemplates =
    !found || found.provider.manual_templates || found.provider.capabilities.includes('sync_templates');
  const current =
    (section === 'senders' && (!canSenders || !hasSenders)) || (section === 'templates' && (!canTemplates || !hasTemplates))
      ? 'connection'
      : section;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="All services">
            <ArrowLeft className="size-3.5" />
            Services
          </Button>
          <span className="flex size-8 items-center justify-center rounded-control bg-accent-subtle text-accent">
            <ChannelIcon channel={channel.channel} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-heading">
              {channel.label ?? channel.provider_label}
            </p>
            <p className="text-2xs text-faint">
              {channel.channel_label} · {channel.provider_label}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge tone={health.tone}>{health.label}</Badge>
          {channel.is_primary ? (
            <Badge tone="accent">
              <Star className="size-2.5" />
              Primary
            </Badge>
          ) : null}
        </div>
      </div>

      <Tabs
        value={current}
        onChange={onSection}
        items={[
          { id: 'connection', label: 'Connection' },
          ...(canSenders && hasSenders
            ? [
                {
                  id: 'senders',
                  label: isWebchat ? 'Websites and apps' : `${found?.channel.sender_label ?? 'Sender'}s`,
                  count: channel.sender_count,
                },
              ]
            : []),
          ...(canTemplates && hasTemplates
            ? [{ id: 'templates', label: 'Templates', count: channel.template_count }]
            : []),
        ]}
      />

      {!found ? (
        <Card>
          <CardBody>
            <p className="text-sm text-muted">
              This service uses a provider this version of Connect does not know ({channel.provider}).
            </p>
          </CardBody>
        </Card>
      ) : current === 'senders' && isWebchat ? (
        <ConnectChatSitesCard channel={channel} />
      ) : current === 'senders' ? (
        <ChannelSendersCard channel={channel} def={found.channel} />
      ) : current === 'templates' ? (
        <ChannelTemplatesCard
          channel={channel}
          def={found.channel}
          provider={found.provider}
          clientName={clientName}
        />
      ) : (
        <ServiceConnectionCard
          channel={channel}
          def={found.channel}
          provider={found.provider}
          clientName={clientName}
          onRemoved={onBack}
        />
      )}
    </div>
  );
}
